// Registry validation — fails fast on unsafe or inconsistent showcase configuration.
// The public marketing website must never carry secrets, embedded media, dev-host media
// URLs, active video without a transcript, or an unauthorised "live-katlas-node" status.

export const MEDIA_STATUSES = ["awaiting-media", "approved", "archived"];
export const DEPLOYMENT_STATUSES = [
  "showcase",
  "live-katlas-node",
  "simulation",
  "concept",
  "archived",
];

const MEDIA_URL_FIELDS = ["videoUrl", "posterUrl", "captionsUrl"];

// Anything that looks like a credential must never live in the registry.
const SECRET_PATTERNS = [
  /bearer\s+[A-Za-z0-9._-]{6,}/i,
  /KATLAS_BEARER_TOKEN/i,
  /KATLAS_API_(KEY|TOKEN|BASE_URL)/i,
  /authorization\s*:/i,
  /\bsk-[A-Za-z0-9]{16,}\b/,
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{6,}\b/, // JWT
  /\b(secret|password|api[_-]?key|access[_-]?token|client[_-]?secret)\b\s*[:=]\s*['"]?[A-Za-z0-9._-]{12,}/i,
];

// Production media URLs must originate from the public media bucket, never a dev host.
const FORBIDDEN_HOST_PATTERNS = [
  /^localhost$/i,
  /^127\.\d+\.\d+\.\d+$/,
  /(^|\.)emergentagent\.(com|net)$/i,
  /(^|\.)emergent\.host$/i,
  /(^|\.)r2\.dev$/i,
];

const isString = (v) => typeof v === "string";
const isDataUrl = (v) => isString(v) && /^data:/i.test(v.trim());
const isAbsolute = (v) => isString(v) && /^https?:\/\//i.test(v.trim());

function hostOf(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

function collectStrings(value, out) {
  if (isString(value)) out.push(value);
  else if (Array.isArray(value)) value.forEach((v) => collectStrings(v, out));
  else if (value && typeof value === "object")
    Object.values(value).forEach((v) => collectStrings(v, out));
}

export function validateShowcase(entry, opts = {}) {
  const errors = [];
  const id = entry?.id || "(no id)";

  // 1. Enum integrity
  if (!MEDIA_STATUSES.includes(entry.mediaStatus)) {
    errors.push(`${id}: invalid mediaStatus "${entry.mediaStatus}".`);
  }
  if (!DEPLOYMENT_STATUSES.includes(entry.deploymentStatus)) {
    errors.push(`${id}: invalid deploymentStatus "${entry.deploymentStatus}".`);
  }

  // 2. No secret-like values anywhere in the entry
  const strings = [];
  collectStrings(entry, strings);
  for (const s of strings) {
    for (const rx of SECRET_PATTERNS) {
      if (rx.test(s)) {
        errors.push(`${id}: secret-like value detected in registry (matched ${rx}).`);
        break;
      }
    }
  }

  // 3. No data URLs / embedded base64 media
  for (const f of MEDIA_URL_FIELDS) {
    if (isDataUrl(entry[f])) {
      errors.push(`${id}: ${f} must not be a data: URL / embedded base64.`);
    }
  }

  // 4. Media URLs must not use localhost / Emergent / r2.dev dev hosts.
  //    Relative "/media/..." dev-fallback paths are allowed.
  for (const f of MEDIA_URL_FIELDS) {
    const url = entry[f];
    if (isAbsolute(url)) {
      const host = hostOf(url);
      if (FORBIDDEN_HOST_PATTERNS.some((rx) => rx.test(host))) {
        errors.push(`${id}: ${f} uses a forbidden dev/production host "${host}".`);
      }
    }
  }

  // 5. An active video card must have an accessible transcript.
  const isActiveVideo =
    entry.kind !== "placeholder" &&
    entry.mediaStatus === "approved" &&
    Boolean(entry.videoUrl);
  if (isActiveVideo) {
    const hasTranscript = Array.isArray(entry.transcript) && entry.transcript.length > 0;
    if (!hasTranscript) {
      errors.push(`${id}: active video card requires a non-empty transcript.`);
    }
  }

  // 6. "live-katlas-node" requires an explicit approved configuration flag.
  if (entry.deploymentStatus === "live-katlas-node") {
    if (entry.liveNodeApproved !== true || opts.liveNodeApproved !== true) {
      errors.push(
        `${id}: deploymentStatus "live-katlas-node" requires entry.liveNodeApproved === true AND an operator-approved configuration. The public website must not connect to the KATLAS signing node.`
      );
    }
  }

  return errors;
}

export function validateRegistry(entries, opts = {}) {
  const errors = entries.flatMap((e) => validateShowcase(e, opts));
  return { valid: errors.length === 0, errors };
}

export function assertValidRegistry(entries, opts = {}) {
  const { valid, errors } = validateRegistry(entries, opts);
  if (!valid) {
    throw new Error("Invalid showcase registry:\n - " + errors.join("\n - "));
  }
  return true;
}
